import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  LayoutGrid,
  Newspaper,
  CalendarClock,
  UserRound,
  ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

const HIDDEN_ON = ["/admin", "/auth", "/reset-password", "/payment"];

export const BottomTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hash, setHash] = useState(window.location.hash);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, [location.pathname]);
  useEffect(() => {
    setHash(window.location.hash);
  }, [location]);

  if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

  const section = (id) => () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else {
      scrollToId(id);
    }
    window.history.replaceState(null, "", `/${id}`);
    setHash(id);
  };
  const onHome = location.pathname === "/";
  const tabs = [
    {
      id: "home",
      label: "Home",
      icon: Home,
      active: onHome && (!hash || hash === "#top"),
      go: () => {
        navigate("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.replaceState(null, "", "/");
        setHash("");
      },
    },
    {
      id: "board",
      label: "Shares",
      icon: LayoutGrid,
      active:
        (onHome && hash === "#opportunities") ||
        location.pathname.startsWith("/company"),
      go: section("#opportunities"),
    },
    {
      id: "insights",
      label: "Insights",
      icon: Newspaper,
      active:
        (onHome && hash === "#insights") ||
        location.pathname.startsWith("/insights"),
      go: section("#insights"),
    },
    {
      id: "consult",
      label: "Consult",
      icon: CalendarClock,
      active: onHome && hash === "#consult",
      go: section("#consult"),
    },
    user?.role === "admin"
      ? {
          id: "account",
          label: "Desk",
          icon: ShieldCheck,
          active: location.pathname.startsWith("/admin"),
          go: () => navigate("/admin"),
        }
      : {
          id: "account",
          label: user ? "Account" : "Sign in",
          icon: UserRound,
          active: location.pathname.startsWith("/client"),
          go: () => navigate(user ? "/client/dashboard" : "/client/login"),
        },
  ];

  return (
    <>
      <div aria-hidden className="h-[68px] md:hidden" />
      <nav
        data-testid="bottom-tab-bar"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur-xl md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid grid-cols-5">
          {tabs.map(({ id, label, icon: Icon, active, go }) => (
            <li key={id}>
              <button
                data-testid={`tab-${id}`}
                onClick={go}
                className={`flex w-full flex-col items-center gap-1 py-2.5 transition-colors ${active ? "text-[#6D28D9]" : "text-slate-500 hover:text-slate-800"}`}
              >
                <span
                  className={`flex h-7 w-11 items-center justify-center rounded-full transition-colors ${active ? "bg-[#6D28D9]/10" : ""}`}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="font-mono2 text-[9px] uppercase tracking-[0.12em]">
                  {label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};
